import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { register } from '../features/authSlice';
import Button from '../components/common/Button';
import { ROUTES } from '../constants';

const RegisterPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, user } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (user) {
      if (redirectTo) {
        navigate(redirectTo);
      } else if (user.role === 'admin') {
        navigate(ROUTES.ADMIN);
      } else {
        navigate(ROUTES.ACCOUNT);
      }
    }
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    await dispatch(register({ name, email, phone, password }));
  };

  const displayError = localError || error;

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: 4,
          borderRadius: 4,
          boxShadow: '0 18px 45px rgba(0,0,0,0.08)',
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h4" sx={{ mb: 1 }}>
          Create account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sign up to start shopping and track your orders.
        </Typography>

        <TextField
          label="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          label="Confirm Password"
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          required
          margin="normal"
        />

        {displayError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {displayError}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          loading={loading}
          sx={{ mt: 3, mb: 1 }}
        >
          Sign up
        </Button>

        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <RouterLink to={redirectTo ? `${ROUTES.LOGIN}?redirect=${encodeURIComponent(redirectTo)}` : ROUTES.LOGIN}>Log in</RouterLink>
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterPage;
